import React from 'react';
import * as XLSX from 'xlsx';
import { Upload, message as messageApi } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { mapExcelRowsToFields } from '../../utils/excelHelper';

const { Dragger } = Upload;

interface UploadFormProps {
  columnConfig: any[];
  onRowsUpdated: (rows: any[]) => void;
  moduleTitle: string;
}

const UploadForm: React.FC<UploadFormProps> = ({
  columnConfig,
  onRowsUpdated,
  moduleTitle,
}) => {
  const [message, contextHolder] = messageApi.useMessage();
  const expectedFileName = moduleTitle.replace(/\s+/g, '-') + '-upload.xlsx';
  const expectedHeaders = columnConfig.map((col) => col.headerName);

  const handleUpload = (file: File) => {
    // ✅ Step 1: Validate file name
    const expectedFilePrefix = moduleTitle.replace(/\s+/g, '-') + '-upload'; 
    const uploadedNameWithoutExt = file.name.replace(/\.xlsx$/i, ''); 
    if (!uploadedNameWithoutExt.startsWith(expectedFilePrefix)) {
      message.error(
        `Invalid file name.\nIt should be named exactly "${expectedFilePrefix}.xlsx" or start with it (e.g., "${expectedFilePrefix} (1).xlsx").`, 3
      );
      return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const json: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        // ✅ Step 2: Validate column headers
        const sheetHeaders = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] as string[];
        const missingHeaders = expectedHeaders.filter((header) => !sheetHeaders.includes(header));
        //const extraHeaders = sheetHeaders.filter((header) => !expectedHeaders.includes(header));

        if (missingHeaders.length > 0 /* || extraHeaders.length > 0 */) {
          message.error(
            `Uploaded Excel columns do not match the expected format. Please correct: ${missingHeaders.join(', ')}`,
          );
          return;
        }

        // ✅ Step 3: Validate row count
        if (json.length === 0) {
          message.error('Excel file contains no data rows.', 3);
          return;
        }

        // ✅ Final: Map and return rows
        const validatedRows = mapExcelRowsToFields(columnConfig, json);
        onRowsUpdated(validatedRows);
      } catch (error) {
        console.error('Excel processing error:', error);
        message.error('Failed to read the Excel file.');
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent default upload behavior
  };

  return (
    <>
      {contextHolder}
      <Dragger
        beforeUpload={handleUpload}
        showUploadList={false}
        multiple={false}
        className="h-full flex flex-col"
        accept=".xlsx"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
        <p className="ant-upload-hint">
          Make sure the file is named <strong>{expectedFileName}</strong> and matches the required
          Excel format.
        </p>
      </Dragger>
    </>
  );
};

export default UploadForm;
